// src/pages/tournament/TournamentDetails.jsx
// When the tournament was created and took place, how many courts there are, and notes. Shown as one
// line (plus the notes); "Edit details" opens the form.
import React, { useState } from 'react';
import { useConfirm } from '../../components/ConfirmDialog';
import { LockIcon } from '../../components/icons';
import { formatDateRange, formatDay } from '../../utils/time';

const MAX_NOTES_LENGTH = 500; // keep in sync with Tournament.MAX_NOTES_LENGTH in backend/entities.py
const MAX_COURTS = 50; // and Tournament.MAX_COURTS

function TournamentDetails({ details, onSave, onSetFinished }) {
    const confirm = useConfirm();
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [togglingFinished, setTogglingFinished] = useState(false);
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    const [courts, setCourts] = useState('');
    const [notes, setNotes] = useState('');

    const dates = formatDateRange(details.startDate, details.endDate);
    const created = formatDay(details.createdAt);

    const open = () => {
        setStart(details.startDate || '');
        setEnd(details.endDate || '');
        setCourts(details.courts ? String(details.courts) : '');
        setNotes(details.notes || '');
        setEditing(true);
    };

    const endBeforeStart = Boolean(start && end && end < start);
    const courtsInvalid = courts !== '' && !(Number.isInteger(Number(courts)) && Number(courts) >= 1 && Number(courts) <= MAX_COURTS);
    const canSave = !endBeforeStart && !courtsInvalid && !saving;

    const save = async (event) => {
        event.preventDefault();
        if (!canSave) return;
        setSaving(true);
        const saved = await onSave({
            startDate: start || null,
            endDate: end || null,
            courts: courts === '' ? null : Number(courts),
            notes,
        });
        setSaving(false);
        if (saved) setEditing(false);
    };

    const toggleFinished = async () => {
        if (!details.finished) {
            const confirmed = await confirm({
                title: 'Mark this tournament as finished?',
                message: 'Players, rounds, matches and scores will be locked. Reopen it any time to make changes again.',
                confirmLabel: 'Mark as finished',
                danger: false,
            });
            if (!confirmed) return;
        }
        setTogglingFinished(true);
        await onSetFinished(!details.finished);
        setTogglingFinished(false);
    };

    if (editing) {
        return (
            <section className="card details-card" aria-label="Tournament details">
                <form onSubmit={save}>
                    <div className="details-grid">
                        <label className="field">
                            <span>First day</span>
                            <input className="input" type="date" value={start} onChange={(event) => setStart(event.target.value)} />
                        </label>
                        <label className="field">
                            <span>Last day</span>
                            <input className="input" type="date" value={end} min={start || undefined} onChange={(event) => setEnd(event.target.value)} />
                        </label>
                        <label className="field">
                            <span>Courts</span>
                            <input
                                className="input"
                                type="number"
                                min="1"
                                max={MAX_COURTS}
                                inputMode="numeric"
                                placeholder="No limit"
                                value={courts}
                                onChange={(event) => setCourts(event.target.value)}
                            />
                        </label>
                    </div>
                    <p className="hint">
                        Courts limit how many matchups are played at the same time in a round. Who sits out is decided
                        fairly: the players who have played the most games so far sit out first (then the ones with the
                        fewest breaks), so the breaks are shared out. Leave it empty for no limit. It applies to rounds
                        you create from now on.
                    </p>
                    {endBeforeStart && (
                        <p className="form-error" role="alert">
                            The last day cannot be before the first day.
                        </p>
                    )}
                    {courtsInvalid && (
                        <p className="form-error" role="alert">
                            Courts must be a whole number from 1 to {MAX_COURTS}.
                        </p>
                    )}
                    <div className="field">
                        <label htmlFor="tournament-notes">Notes</label>
                        <textarea
                            id="tournament-notes"
                            className="input textarea"
                            rows={3}
                            maxLength={MAX_NOTES_LENGTH}
                            placeholder="Venue, rules, anything worth remembering"
                            value={notes}
                            aria-describedby="tournament-notes-count"
                            onChange={(event) => setNotes(event.target.value)}
                        />
                        <span id="tournament-notes-count" className="hint">
                            {notes.length}/{MAX_NOTES_LENGTH}
                        </span>
                    </div>
                    <div className="details-actions">
                        <button type="button" className="btn" onClick={() => setEditing(false)}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={!canSave}>
                            Save details
                        </button>
                    </div>
                </form>
            </section>
        );
    }

    return (
        <section className="card details-card" aria-label="Tournament details">
            {details.finished && (
                <p className="finished-banner">
                    <LockIcon size={16} /> This tournament is finished. Players, rounds and scores are locked.
                </p>
            )}
            <div className="details-summary">
                <ul className="details-facts">
                    <li>
                        <span className="label">Played</span> {dates || <span className="muted">no dates set</span>}
                    </li>
                    <li>
                        <span className="label">Courts</span>{' '}
                        {details.courts ? details.courts : <span className="muted">no limit</span>}
                    </li>
                    {created && (
                        <li>
                            <span className="label">Created</span> {created}
                        </li>
                    )}
                </ul>
                <div className="details-actions-row">
                    <button className="btn btn-sm" onClick={open}>
                        Edit details
                    </button>
                    {onSetFinished && (
                        <button className="btn btn-sm" onClick={toggleFinished} disabled={togglingFinished}>
                            {details.finished ? 'Reopen' : 'Mark as finished'}
                        </button>
                    )}
                </div>
            </div>
            {details.notes && <p className="details-notes">{details.notes}</p>}
        </section>
    );
}

export default TournamentDetails;
